import { useEffect, useState, lazy, Suspense } from 'react';
import { getBakedPOIs, DISCOVERY_RADIUS_MI } from '../services/regionService';
import { getEnrichmentForPlaces } from '../services/enrichmentService';
import { getPlaceFacts } from '../services/googlePlacesService';
import { getRelevantPlaces } from '../services/placeRanking';
import { getReportedPlaceIds } from '../services/contributionService';
import { track } from '../services/firebase';
import POICard from './POICard';
import LocalForecast from './LocalForecast';
import { getSourceAttribute } from '../utils/placeAttributes';

const NearbyMap = lazy(() => import('./NearbyMap'));
const CHIP_LABELS = {
  difficulty: { easy: 'Easy', moderate: 'Moderate', hard: 'Hard' },
  parking: {
    yes: 'Parking listed',
    limited: 'Limited parking',
    no: 'No parking listed',
  },
  restrooms: { yes: 'Restrooms listed', seasonal: 'Seasonal toilets', no: 'No restrooms listed' },
  dog_friendly: {
    yes: 'Dogs listed as allowed',
    leashed: 'Dogs on leash',
    no: 'No dogs',
  },
  permit_or_fee: {
    fee: 'Entry fee listed',
    permit: 'Permit listed',
    license: 'License required',
  },
};

function chipsFor(record, activityId) {
  return Object.entries(CHIP_LABELS)
    .filter(([key]) => key !== 'difficulty' || activityId === 'hiking')
    .flatMap(([key, labels]) => {
      const attr = getSourceAttribute(record, key);
      const label = labels[String(attr?.value ?? '').toLowerCase()];
      return label && attr?.confidence !== 'low' ? [label] : [];
    })
    .slice(0, 3);
}

function PlacePhoto({ place, className, children }) {
  const [facts, setFacts] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setFacts(null);
    setFailed(false);
    getPlaceFacts(place)
      .then((data) => {
        if (active) setFacts(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [place.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={`${className} relative overflow-hidden bg-gradient-to-br from-[#b3c3aa] via-[#879e7e] to-[#405e4d]`}
    >
      {facts?.photoUri && !failed ? (
        <img
          src={facts.photoUri}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <svg
          aria-hidden="true"
          viewBox="0 0 300 150"
          className="absolute inset-0 h-full w-full"
          preserveAspectRatio="xMidYMid slice"
        >
          <path
            d="M0 130 60 55 130 115 220 30 300 120v40H0Z"
            fill="#3b5948"
            opacity=".35"
          />
          <path
            d="M0 160 90 100 200 140 270 90 320 130v40H0Z"
            fill="#2d4538"
            opacity=".4"
          />
        </svg>
      )}
      {children}
    </div>
  );
}

export default function ActivityDiscoveryView({
  activity,
  area,
  forecast,
  onClose,
}) {
  const [places, setPlaces] = useState(null);
  const [enrichment, setEnrichment] = useState(null);
  const [openPlace, setOpenPlace] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setPlaces(null);
    setEnrichment(null);
    track('discovery_opened', { activity: activity.id });
    (async () => {
      try {
        const baked = await getBakedPOIs(
          area.lat,
          area.lon,
          DISCOVERY_RADIUS_MI,
        );
        const reported = getReportedPlaceIds();
        const pool = (baked || []).filter((place) => !reported.has(place.id));
        const map = await getEnrichmentForPlaces(pool).catch(() => new Map());
        if (active) {
          setEnrichment(map);
          setPlaces(getRelevantPlaces(activity.id, pool, map, 12));
        }
      } catch {
        if (active) setPlaces([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [activity.id, area.lat, area.lon]);

  function openDetail(place) {
    track('discovery_place_opened', {
      activity: activity.id,
      place_type: place.type,
    });
    setOpenPlace(place);
  }

  if (mapOpen && places?.length) {
    return (
      <Suspense
        fallback={
          <div role="status" className="fixed inset-0 z-50 bg-white p-6">
            Loading map...
          </div>
        }
      >
        <NearbyMap
          lat={area.lat}
          lon={area.lon}
          pois={places}
          title={`${activity.name} near ${area.city}`}
          hideFilters
          activityId={activity.id}
          onClose={() => setMapOpen(false)}
        />
      </Suspense>
    );
  }

  const hero = places?.[0];
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#fafbf8]">
      <header className="sticky top-0 z-10 border-b border-stone-200 bg-white/95 px-4 py-3 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <button
            onClick={onClose}
            aria-label="Back"
            className="rounded-full px-3 py-2 text-xl hover:bg-stone-100"
          >
            &larr;
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold">
              {activity.name} near {area.city}
            </h1>
            <p className="mt-0.5 text-xs text-stone-600">
              {places
                ? `${places.length} places in our guide`
                : 'Finding places...'}
            </p>
          </div>
          {places?.length > 0 && (
            <button
              onClick={() => setMapOpen(true)}
              className="rounded-full border border-stone-200 px-4 py-2 text-sm font-medium hover:bg-stone-50"
            >
              Map
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-5 pb-10 pt-5">
        {forecast && (
          <div className="mb-5">
            <LocalForecast area={area} forecast={forecast} compact />
          </div>
        )}
        {places === null && (
          <p
            role="status"
            className="rounded-2xl bg-white p-6 text-sm text-stone-600"
          >
            Loading nearby places...
          </p>
        )}
        {places?.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-6">
            <h2 className="font-semibold">No places to show here yet</h2>
            <p className="mt-2 text-sm leading-relaxed text-stone-600">
              Our guide covers the Hudson Valley. We couldn&rsquo;t load any{' '}
              {activity.name.toLowerCase()} places within {DISCOVERY_RADIUS_MI}{' '}
              miles of {area.city}.
            </p>
            <button
              onClick={onClose}
              className="mt-4 text-sm font-medium underline underline-offset-4"
            >
              Back to change town
            </button>
          </div>
        )}
        {hero && (
          <>
            <p className="mb-3 text-xs leading-relaxed text-stone-600">
              Within {DISCOVERY_RADIUS_MI} miles. Distances are straight-line,
              not driving distance. Access and opening hours are not live.
            </p>
            <button
              onClick={() => openDetail(hero)}
              className="w-full overflow-hidden rounded-2xl border border-stone-200 bg-white text-left shadow-sm transition-shadow hover:shadow-md"
            >
              <PlacePhoto place={hero} className="h-52 w-full">
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="mb-1 text-xs font-medium uppercase tracking-widest text-white/80">
                    A place to start
                  </p>
                  <h2 className="text-2xl font-semibold leading-tight text-white">
                    {hero.name}
                  </h2>
                  <p className="mt-1 text-sm text-white/90">
                    {hero.distance.toFixed(1)} miles away
                  </p>
                </div>
              </PlacePhoto>
              <div className="p-5">
                {enrichment?.get(hero.id)?.summary && (
                  <p className="mb-3 text-sm leading-relaxed text-stone-600">
                    {enrichment.get(hero.id).summary}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {chipsFor(enrichment?.get(hero.id), activity.id).map(
                    (label) => (
                      <span
                        key={label}
                        className="rounded-full bg-[#f0f3ec] px-3 py-1 text-xs text-[#46563e]"
                      >
                        {label}
                      </span>
                    ),
                  )}
                </div>
                <p className="mt-3 text-sm font-semibold">
                  See place details <span aria-hidden="true">&rarr;</span>
                </p>
              </div>
            </button>
          </>
        )}
        {places?.length > 1 && (
          <section className="mt-6 space-y-3" aria-label="More places">
            <h2 className="text-sm font-semibold">More places to explore</h2>
            {places.slice(1).map((place) => (
              <button
                key={place.id}
                onClick={() => openDetail(place)}
                className="flex w-full items-center gap-4 rounded-2xl border border-stone-200 bg-white p-3 text-left transition-shadow hover:shadow-md"
              >
                <PlacePhoto
                  place={place}
                  className="h-20 w-20 shrink-0 rounded-xl"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold leading-snug">
                    {place.name}
                  </h3>
                  <p className="mt-1 text-xs text-stone-600">
                    {place.distance.toFixed(1)} miles away
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-stone-600">
                    {chipsFor(enrichment?.get(place.id), activity.id).join(
                      ' / ',
                    ) || 'See place details'}
                  </p>
                </div>
                <span aria-hidden="true" className="text-stone-500">
                  &rarr;
                </span>
              </button>
            ))}
          </section>
        )}
      </main>

      {openPlace && (
        <div className="fixed inset-0 z-[60]">
          <button
            onClick={() => setOpenPlace(null)}
            className="absolute inset-0 bg-black/35"
            aria-label="Close place details"
          />
          <POICard
            poi={openPlace}
            onClose={() => setOpenPlace(null)}
            activityId={activity.id}
          />
        </div>
      )}
    </div>
  );
}
